<!-- Hero Section -->
<section class="min-h-[80vh] relative pt-32 pb-20 md:pt-40 md:pb-28 overflow-hidden bg-linear-to-br from-charcoal via-primary to-charcoal flex items-center">
	<!-- Background Pattern -->
	<div class="absolute inset-0 opacity-10" style="background-image: radial-gradient(circle at 1px 1px, #caa78a 1px, transparent 0); background-size: 40px 40px;"></div>

	<!-- Decorative Elements -->
	<div class="absolute top-20 left-10 w-72 h-72 bg-secondary/20 rounded-full blur-3xl animate-pulse"></div>
	<div class="absolute bottom-20 right-10 w-96 h-96 bg-secondary/10 rounded-full blur-3xl animate-pulse" style="animation-delay: 1s;"></div>

	<div class="relative max-w-4xl mx-auto px-6 md:px-8 text-center h-full flex flex-col justify-center items-center">
		<div class="flex items-center justify-center gap-4 mb-6">
			<div class="w-16 h-px bg-secondary"></div>
			<span class="text-secondary text-sm font-medium tracking-[0.25em] uppercase">{{ heroTagline() }}</span>
			<div class="w-16 h-px bg-secondary"></div>
		</div>
		<h1 class="font-cenzo text-4xl md:text-6xl lg:text-7xl font-bold text-white leading-[1.1] mb-8">
			{{ heroTitle() }}
		</h1>
		<p class="text-white/70 text-lg md:text-xl leading-relaxed max-w-2xl mx-auto">
			{{ heroDescription() }}
		</p>
	</div>
</section>

<!-- Our Purpose - Full Height with Image Left -->
<section class="min-h-screen flex items-center bg-white py-16">
	<div class="max-w-6xl mx-auto px-6 md:px-8 w-full">
		<div class="grid md:grid-cols-2 gap-12 md:gap-16 items-center">
			<!-- Image on Left -->
			<div class="relative">
				<!-- Decorative frame -->
				<div class="absolute -top-4 -left-4 w-full h-full border-2 border-secondary/30 rounded-2xl"></div>
				<div class="absolute -bottom-4 -right-4 w-32 h-32 bg-secondary/20 rounded-full blur-2xl"></div>

				<!-- Main Image -->
				<div class="relative rounded-2xl overflow-hidden shadow-2xl">
					<img
						[src]="purposeImage()"
						alt="OG Hair Studio"
						class="w-full h-[400px] md:h-[550px] object-cover"
					/>
					<!-- Gradient overlay -->
					<div class="absolute inset-0 bg-linear-to-t from-primary/40 to-transparent"></div>
				</div>

				<!-- Floating badge -->
				<div class="absolute -bottom-6 -right-6 bg-primary text-white px-6 py-4 rounded-xl shadow-xl">
					<p class="font-cenzo text-3xl font-bold">{{ purposeYears() }}</p>
					<p class="text-white/80 text-sm">{{ purposeYearsText() }}</p>
				</div>
			</div>

			<!-- Content on Right -->
			<div>
				<p class="text-secondary text-sm font-medium tracking-[0.25em] uppercase mb-6">{{ purposeTagline() }}</p>
				<h2 class="font-cenzo text-3xl md:text-4xl lg:text-5xl font-bold text-primary leading-[1.15] mb-8">
					{{ purposeTitle() }}
				</h2>
				<div class="space-y-5 text-charcoal/70 text-lg leading-relaxed">
					<p>
						{{ purposeParagraph1() }}
					</p>
					<p>
						{{ purposeParagraph2() }}
					</p>
				</div>
			</div>
		</div>
	</div>
</section>

<!-- Our Values - Full Height with Primary Background & Animations -->
<section class="min-h-[80vh] flex items-center bg-primary relative overflow-hidden">
	<!-- Background Pattern -->
	<div class="absolute inset-0 opacity-10" style="background-image: radial-gradient(circle at 1px 1px, #caa78a 1px, transparent 0); background-size: 40px 40px;"></div>

	<!-- Animated Decorative Elements -->
	<div class="absolute top-20 left-10 w-72 h-72 bg-secondary/15 rounded-full blur-3xl animate-pulse"></div>
	<div class="absolute bottom-20 right-10 w-96 h-96 bg-secondary/10 rounded-full blur-3xl animate-pulse" style="animation-delay: 1.5s;"></div>
	<div class="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-white/5 rounded-full blur-3xl"></div>

	<div class="relative max-w-5xl mx-auto px-6 md:px-8 py-20 w-full">
		<div class="text-center mb-16 md:mb-20">
			<p class="text-secondary text-sm font-medium tracking-[0.25em] uppercase mb-6 animate-fade-in">{{ valuesTagline() }}</p>
			<h2 class="font-cenzo text-3xl md:text-4xl lg:text-5xl font-bold text-white animate-fade-in-up">{{ valuesTitle() }}</h2>
		</div>

		<div class="grid md:grid-cols-3 gap-12 md:gap-16">
			<!-- Value 1 -->
			<div class="text-center md:text-left group value-card" style="animation-delay: 0.2s;">
				<div class="w-16 h-16 bg-secondary/20 rounded-full flex items-center justify-center mb-6 mx-auto md:mx-0 group-hover:bg-secondary/40 group-hover:scale-110 transition-all duration-500">
					<span class="text-secondary text-2xl font-bold">01</span>
				</div>
				<h3 class="font-cenzo text-2xl font-bold text-white mb-4 group-hover:text-secondary transition-colors duration-300">{{ value1Title() }}</h3>
				<p class="text-white/70 leading-relaxed group-hover:text-white/90 transition-colors duration-300">
					{{ value1Description() }}
				</p>
			</div>

			<!-- Value 2 -->
			<div class="text-center md:text-left group value-card" style="animation-delay: 0.4s;">
				<div class="w-16 h-16 bg-secondary/20 rounded-full flex items-center justify-center mb-6 mx-auto md:mx-0 group-hover:bg-secondary/40 group-hover:scale-110 transition-all duration-500">
					<span class="text-secondary text-2xl font-bold">02</span>
				</div>
				<h3 class="font-cenzo text-2xl font-bold text-white mb-4 group-hover:text-secondary transition-colors duration-300">{{ value2Title() }}</h3>
				<p class="text-white/70 leading-relaxed group-hover:text-white/90 transition-colors duration-300">
					{{ value2Description() }}
				</p>
			</div>

			<!-- Value 3 -->
			<div class="text-center md:text-left group value-card" style="animation-delay: 0.6s;">
				<div class="w-16 h-16 bg-secondary/20 rounded-full flex items-center justify-center mb-6 mx-auto md:mx-0 group-hover:bg-secondary/40 group-hover:scale-110 transition-all duration-500">
					<span class="text-secondary text-2xl font-bold">03</span>
				</div>
				<h3 class="font-cenzo text-2xl font-bold text-white mb-4 group-hover:text-secondary transition-colors duration-300">{{ value3Title() }}</h3>
				<p class="text-white/70 leading-relaxed group-hover:text-white/90 transition-colors duration-300">
					{{ value3Description() }}
				</p>
			</div>
		</div>
	</div>
</section>

<!-- Founder Section -->
<section class="py-24 md:py-32 bg-white">
	<div class="max-w-6xl mx-auto px-6 md:px-8">
		<div class="grid md:grid-cols-2 gap-12 md:gap-20 items-center">
			<!-- Image -->
			<div class="order-2 md:order-1">
				<div class="aspect-4/5 overflow-hidden rounded-2xl">
					<img
						[src]="founderImage()"
						[alt]="founderName() + ', ' + founderRole()"
						class="w-full h-full object-cover"
					/>
				</div>
			</div>

			<!-- Content -->
			<div class="order-1 md:order-2">
				<p class="text-secondary text-sm font-medium tracking-[0.25em] uppercase mb-6">{{ founderTagline() }}</p>
				<h2 class="font-cenzo text-3xl md:text-4xl font-bold text-primary mb-4">{{ founderName() }}</h2>
				<p class="text-secondary font-medium mb-8">{{ founderRole() }}</p>

				<div class="space-y-5 text-charcoal/70 text-lg leading-relaxed">
					<p>
						{{ founderBio1() }}
					</p>
					<p>
						{{ founderBio2() }}
					</p>
					<p>
						{{ founderQuote() }}
					</p>
				</div>
			</div>
		</div>
	</div>
</section>

<!-- Our Team -->
<section class="py-24 md:py-32" style="background-color: #eaeae7;">
	<div class="max-w-6xl mx-auto px-6 md:px-8">
		<div class="text-center mb-12 md:mb-16">
			<p class="text-secondary text-sm font-medium tracking-[0.25em] uppercase mb-6">{{ teamTagline() }}</p>
			<h2 class="font-cenzo text-3xl md:text-4xl lg:text-5xl font-bold text-primary mb-6">{{ teamTitle() }}</h2>
			<p class="text-charcoal/70 text-lg max-w-2xl mx-auto">
				{{ teamDescription() }}
			</p>
		</div>
	</div>

	<!-- Single Image Display -->
	<div class="relative mx-auto px-6" [class]="currentTeamIndex() === 0 ? 'max-w-6xl' : 'max-w-2xl'">

		<!-- Left Button -->
		@if (currentTeamIndex() > 0) {
		<div class="absolute left-0 top-1/2 -translate-y-1/2 z-10">
			<button (click)="scrollTeam('left')" class="team-glass-btn">
				<svg class="w-4 h-4 sm:w-5 sm:h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
					<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M15 19l-7-7 7-7"/>
				</svg>
			</button>
		</div>
		}

		<!-- Single Card Display -->
		<div class="flex justify-center py-2">
			<!-- Team Image (index 0) - Full Width -->
			@if (currentTeamIndex() === 0) {
			<div class="team-card rounded-2xl overflow-hidden shadow-xl h-[280px] sm:h-[340px] md:h-[400px] lg:h-[440px] w-full max-w-4xl">
				<img [src]="teamImage()" alt="OG Hair Studio Team"
					class="w-full h-full object-cover rounded-2xl"/>
			</div>
			} @else {
			<!-- Team Member Card - Fixed Size -->
			@if (getCurrentMember(); as member) {
			<div class="team-card rounded-2xl overflow-hidden relative group cursor-pointer shadow-xl h-[320px] sm:h-[380px] md:h-[440px] w-[320px] sm:w-[360px] md:w-[400px]">
				<div class="relative w-full h-full overflow-hidden bg-charcoal/5">
					@if (member.image_url) {
					<img [src]="member.image_url" [alt]="member.name"
						class="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"/>
					} @else {
					<div class="w-full h-full bg-gradient-to-br from-primary/10 to-secondary/10 flex items-center justify-center">
						<span class="text-6xl font-cenzo font-bold text-primary/20">{{ member.name.charAt(0) }}</span>
					</div>
					}

					<!-- Hover Overlay -->
					<div class="absolute inset-0 bg-gradient-to-t from-primary via-primary/70 to-transparent opacity-0 group-hover:opacity-100 transition-all duration-500 flex flex-col justify-end p-6">
						<div class="transform translate-y-4 group-hover:translate-y-0 transition-transform duration-500">
							<p class="text-secondary text-sm font-semibold tracking-wider uppercase mb-1">{{ member.title }}</p>
							<h3 class="font-cenzo text-xl md:text-2xl font-bold text-white mb-2">{{ member.name }}</h3>
							@if (member.description) {
							<p class="text-white/80 text-sm leading-relaxed line-clamp-3">{{ member.description }}</p>
							}
						</div>
					</div>

					<!-- Bottom Name Bar -->
					<div class="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-charcoal/80 via-charcoal/40 to-transparent p-5 group-hover:opacity-0 transition-opacity duration-500">
						<h3 class="font-cenzo text-lg font-bold text-white">{{ member.name }}</h3>
						<p class="text-secondary text-sm font-medium">{{ member.title }}</p>
					</div>
				</div>
			</div>
			}
			}
		</div>

		<!-- Right Button -->
		@if (currentTeamIndex() < teamMembers().length) {
		<div class="absolute right-0 top-1/2 -translate-y-1/2 z-10">
			<button (click)="scrollTeam('right')" class="team-glass-btn">
				<svg class="w-4 h-4 sm:w-5 sm:h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
					<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M9 5l7 7-7 7"/>
				</svg>
			</button>
		</div>
		}

		<!-- Indicator Dots -->
		<div class="flex justify-center gap-2 mt-6">
			@for (i of getTeamIndices(); track i) {
			<button (click)="currentTeamIndex.set(i)"
				class="w-2.5 h-2.5 rounded-full transition-all duration-300"
				[class]="currentTeamIndex() === i ? 'bg-primary scale-125' : 'bg-charcoal/30 hover:bg-charcoal/50'">
			</button>
			}
		</div>
	</div>
</section>

<!-- Simple CTA -->
<section class="py-24 md:py-32 bg-primary m-5 rounded-lg">
	<div class="max-w-3xl mx-auto px-6 md:px-8 text-center">
		<h2 class="font-cenzo text-3xl md:text-4xl lg:text-5xl font-bold text-mist mb-6">
			{{ ctaTitle() }}
		</h2>
		<p class="text-mist/80 text-lg mb-10 max-w-xl mx-auto">
			{{ ctaDescription() }}
		</p>
		<div class="flex flex-col sm:flex-row gap-4 justify-center">
			<button
				(click)="openBookingModal()"
				class="inline-flex items-center justify-center gap-2 px-8 py-4 bg-secondary text-primary font-bold rounded-full hover:bg-secondary/90 transition-colors">
				Book Consultation
			</button>
			<a [href]="'tel:' + ctaPhone()"
				class="inline-flex items-center justify-center gap-2 px-8 py-4 border-2 border-mist/30 text-mist font-semibold rounded-full hover:bg-mist/10 transition-colors">
				<svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
					<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"/>
				</svg>
				Call Us
			</a>
		</div>
	</div>
</section>

<!-- Booking Modal -->
<app-booking-modal
	*ngIf="showBookingModal()"
	(close)="closeBookingModal()"
/>
